/**
 * StateLabeling — /states/labeling
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "@/hooks/useStateContext";
import { Tag, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { PageHeader, SectionCard, CTAButton } from "@/components/ds";

const LABELS = [
  { key: "low_carbon_champion",  label: "Low Carbon Champion",  color: "bg-emerald-100 text-emerald-700",
    desc: "Emissions intensity significantly below sector average." },
  { key: "climate_aligned",      label: "Climate Aligned",       color: "bg-teal-100 text-teal-700",
    desc: "Demonstrated alignment with climate targets." },
  { key: "transition_pathway",   label: "Transition Pathway",    color: "bg-blue-100 text-blue-700",
    desc: "Active decarbonization plan with evidence." },
  { key: "poa_eligible",         label: "PoA Eligible",          color: "bg-purple-100 text-purple-700",
    desc: "Meets minimum criteria for PoA aggregation." },
  { key: "finance_ready",        label: "Finance Ready",         color: "bg-amber-100 text-amber-700",
    desc: "Portfolio Intelligence: Finance Ready status." },
  { key: "under_review",         label: "Under Review",          color: "bg-slate-100 text-slate-600",
    desc: "Under state-level climate assessment." },
];

export default function StateLabeling() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ entity_type: "organization", entity_id: "", label: "", rationale: "" });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const { stateId, geography: geo, stateParams } = useStateContext();

  const handleAssign = async () => {
    if (!form.entity_id || !form.label) { toast.error("Entity ID and label required."); return; }
    setSaving(true);
    try {
      const res = await client.post("/states/labels", {
        entity_type: form.entity_type,
        entity_id: parseInt(form.entity_id),
        label: form.label,
        rationale: form.rationale,
        geography: geo,
      });
      setResult(res.data);
      toast.success(`Label "${form.label}" assigned.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assignment failed.");
    }
    setSaving(false);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="STATES WORKSPACE"
        title="Labeling & Classification"
        subtitle="Platform classifications — not regulatory certificates"
        backUrl="/states/overview"
        backLabel="State Overview"
      />

      <MetricCard label="Label Types" value={LABELS.length} color="text-slate-900" className="inline-block" />

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3">
        <p className="text-xs text-amber-700">
          These are DeCarbonIQ platform classifications. They are NOT regulatory certifications,
          official government labels, or third-party verified.
        </p>
      </div>

      <SectionCard title="Label Catalog" noPadding>
        {LABELS.map(l => (
          <div key={l.key} className="flex items-center gap-4 px-5 py-3 border-b border-slate-50 last:border-0">
            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full flex-shrink-0 ${l.color}`}>{l.label}</span>
            <p className="text-xs text-slate-500 flex-1">{l.desc}</p>
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Assign Label">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Entity Type</label>
              <select value={form.entity_type} onChange={e => setForm({...form, entity_type: e.target.value})}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl">
                <option value="organization">Organization</option>
                <option value="project">Project</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Entity ID</label>
              <input type="number" value={form.entity_id}
                onChange={e => setForm({...form, entity_id: e.target.value})}
                placeholder="ID number" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Label</label>
            <select value={form.label} onChange={e => setForm({...form, label: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl">
              <option value="">Select label...</option>
              {LABELS.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Rationale (optional)</label>
            <input value={form.rationale} onChange={e => setForm({...form, rationale: e.target.value})}
              placeholder="Basis for this classification..." className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" />
          </div>
          <CTAButton variant="action" icon={Tag} loading={saving} onClick={handleAssign}>
            {saving ? "Assigning..." : "Assign Label"}
          </CTAButton>
        </div>
        {result && (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-emerald-500" />
              <p className="text-sm font-semibold text-emerald-700">
                Label assigned to {result.entity_name}: {result.label}
              </p>
            </div>
            <p className="text-xs text-slate-400 mt-1 italic">{result.disclaimer}</p>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
